import { useEffect, useMemo, useState } from "react";
import { Users, ListChecks, X as XIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import TagInput from "./TagInput";
import api from "@/lib/api";

const AUDIENCE_LABELS = {
  all: "כל הנרשמים (המנויים)",
  tags_or: "לפחות תיוג אחד (OR)",
  tags_and: "כל התיוגים (AND)",
};

/**
 * Audience picker: audience_type + tags + exclude_tags + LIST filter + live preview count.
 * - value: {audience_type, audience_tags, exclude_tags, audience_list_ids}
 * - onChange: (nextValue) => void
 * - suggestions: string[]  distinct tags in system
 *
 * List filter (NEW): when one or more `list_id` values are selected, the audience
 * is narrowed to leads whose `list_id` is in that set (regardless of `audience_type`).
 * Empty list → no list filter (all lists).
 */
export default function AudiencePicker({ value, onChange, suggestions = [], testIdPrefix = "audience" }) {
  const [preview, setPreview] = useState({ loading: true, count: 0 });
  const [lists, setLists] = useState([]);         // [{list_id, count, total, affiliate_name, campaign_slug, ...}]
  const [listsLoading, setListsLoading] = useState(true);

  const set = (patch) => onChange({ ...value, ...patch });
  const selectedListIds = useMemo(() => new Set(value.audience_list_ids || []), [value.audience_list_ids]);

  // Fetch available lists once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/lists");
        if (alive) setLists(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setLists([]);
      } finally {
        if (alive) setListsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setPreview((p) => ({ ...p, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/admin/audience-preview", {
          audience_type: value.audience_type || "all",
          audience_tags: value.audience_tags || [],
          exclude_tags: value.exclude_tags || [],
          audience_list_ids: value.audience_list_ids || [],
        });
        if (alive) setPreview({ loading: false, count: data.count });
      } catch {
        if (alive) setPreview({ loading: false, count: 0 });
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [value.audience_type, JSON.stringify(value.audience_tags || []), JSON.stringify(value.exclude_tags || []), JSON.stringify(value.audience_list_ids || [])]);

  const needsTags = value.audience_type !== "all";

  const toggleListId = (lid) => {
    const cur = value.audience_list_ids || [];
    const next = cur.includes(lid) ? cur.filter((x) => x !== lid) : [...cur, lid];
    set({ audience_list_ids: next });
  };
  const clearLists = () => set({ audience_list_ids: [] });

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-bold mb-2 block">סוג קהל</Label>
        <Select
          value={value.audience_type || "all"}
          onValueChange={(v) => set({ audience_type: v })}
        >
          <SelectTrigger className="h-11" data-testid={`${testIdPrefix}-type-trigger`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(AUDIENCE_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k} data-testid={`${testIdPrefix}-type-${k}`}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {needsTags && (
        <div>
          <Label className="text-sm font-bold mb-2 block">
            תיוגים לכלול
            <span className="text-xs font-normal text-stone-500 ms-2">
              {value.audience_type === "tags_and" ? "(חובה שכל התיוגים יתאימו)" : "(מספיק תיוג אחד)"}
            </span>
          </Label>
          <TagInput
            value={value.audience_tags || []}
            onChange={(tags) => set({ audience_tags: tags })}
            suggestions={suggestions}
            placeholder="תיוגים כמו: hot_lead, vip…"
            testId={`${testIdPrefix}-include-tags`}
          />
        </div>
      )}

      <div>
        <Label className="text-sm font-bold mb-2 block">
          תיוגים לא לכלול
          <span className="text-xs font-normal text-stone-500 ms-2">(לידים עם תיוג זה יסוננו החוצה)</span>
        </Label>
        <TagInput
          value={value.exclude_tags || []}
          onChange={(tags) => set({ exclude_tags: tags })}
          suggestions={suggestions}
          placeholder="תיוגים לסינון…"
          testId={`${testIdPrefix}-exclude-tags`}
        />
      </div>

      {/* NEW: List filter (list_id) */}
      <div data-testid={`${testIdPrefix}-lists-section`}>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-bold flex items-center gap-1.5">
            <ListChecks className="w-4 h-4 text-brand-amber" />
            סינון לפי רשימה (List)
            <span className="text-xs font-normal text-stone-500 ms-1">
              {(value.audience_list_ids || []).length === 0 ? "(ריק = כל הרשימות)" : `(${(value.audience_list_ids || []).length} נבחרו)`}
            </span>
          </Label>
          {(value.audience_list_ids || []).length > 0 && (
            <button
              type="button"
              onClick={clearLists}
              className="text-xs text-stone-500 hover:text-red-600 flex items-center gap-1"
              data-testid={`${testIdPrefix}-lists-clear`}
            >
              <XIcon className="w-3 h-3" /> נקה
            </button>
          )}
        </div>
        {listsLoading ? (
          <div className="text-xs text-stone-400 py-3">טוען רשימות…</div>
        ) : lists.length === 0 ? (
          <div className="text-xs text-stone-500 py-3 bg-stone-50 border border-stone-200 rounded-lg px-3">
            אין רשימות עם לידים במערכת עדיין.
          </div>
        ) : (
          <div className="max-h-60 overflow-y-auto border border-stone-200 rounded-lg divide-y divide-stone-100 bg-white">
            {lists.map((l) => {
              const checked = selectedListIds.has(l.list_id);
              return (
                <label
                  key={l.list_id}
                  className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-stone-50 select-none ${checked ? "bg-amber-50" : ""}`}
                  data-testid={`${testIdPrefix}-list-item-${l.list_id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleListId(l.list_id)}
                    className="w-4 h-4 rounded border-stone-300 text-brand-amber focus:ring-brand-amber"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="ltr text-sm font-mono font-bold text-stone-800">{l.list_id}</span>
                      {l.affiliate_name && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 font-bold">
                          {l.affiliate_name}
                        </span>
                      )}
                      {l.campaign_slug && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600 ltr">
                          {l.campaign_slug}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      <b className="text-emerald-700">{l.count}</b> מנויים פעילים · {l.total} סה&quot;כ
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3" data-testid={`${testIdPrefix}-preview`}>
        <Users className="w-5 h-5 text-brand-amber" />
        <div className="flex-1">
          <div className="text-xs text-stone-600">קהל תואם (רק לידים עם consent_status = subscribed):</div>
          <div className="font-[800] text-lg text-brand-charcoal" data-testid={`${testIdPrefix}-count`}>
            {preview.loading ? "…" : `${preview.count} לידים`}
          </div>
        </div>
      </div>
    </div>
  );
}
